"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ServiceWorkerController = void 0;
const FeaturesService_1 = require("./FeaturesService");
const Utils_1 = require("../helpers/Utils");
const Logger_1 = require("./Logger");
const TelemetryEvents_1 = require("../models/telemetry/TelemetryEvents");
// Will be replaced by webpack plugin with a list of assets to precache
const PRECACHE_ASSETS = ["__PRECACHE_ASSETS__"];
function formatSwError(context) {
    const parts = [context.operation];
    if (context.url) {
        parts.push(`url=${context.url}`);
    }
    if (context.scope) {
        parts.push(`scope=${context.scope}`);
    }
    if (context.state) {
        parts.push(`state=${context.state}`);
    }
    parts.push(Utils_1.Utils.scrubEuii(context.error));
    return parts.join(":");
}
function getSwErrorCategory(err) {
    if (err.name === "SecurityError") {
        return "insecure_context";
    }
    if (err.message.includes("timed out")) {
        return "timeout";
    }
    if (err.message.includes("fetch") || err.message.includes("network")) {
        return "network";
    }
    if (err.message.includes("scope")) {
        return "scope_mismatch";
    }
    if (err.message.includes("script")) {
        return "script_error";
    }
    return "unknown";
}
class ServiceWorkerController {
    static get instance() {
        if (!this._instance) {
            this._instance = new ServiceWorkerController();
        }
        return this._instance;
    }
    run() {
        return __awaiter(this, void 0, void 0, function* () {
            // Setup message listener for metrics from Service Worker
            this.setupMessageListener();
            const featureFlags = FeaturesService_1.default.getFeatureFlags();
            const mode = featureFlags.serviceWorkerRegistration;
            if (!mode) {
                return;
            }
            const normalized = String(mode).toLowerCase();
            if (normalized === "unregister") {
                yield this.unregisterAll();
                return;
            }
            if (normalized === "register") {
                yield this.register();
                return;
            }
            // Any other non-empty string: no-op for now
        });
    }
    unregisterAll() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            if ("serviceWorker" in navigator) {
                let regsToUnregister = [];
                try {
                    const regs = yield navigator.serviceWorker.getRegistrations();
                    if (regs.length === 0) {
                        return;
                    }
                    // Only unregister Service Workers within our scope to avoid affecting
                    // other applications on the same origin (e.g., other Teams features)
                    const ourScope = this.getServiceWorkerScope();
                    regsToUnregister = regs.filter(r => {
                        // Normalize scope URLs for comparison
                        const regScope = new URL(r.scope, window.location.origin).pathname;
                        // Only unregister if the SW scope is within or matches our scope
                        return regScope.startsWith(ourScope);
                    });
                    if (regsToUnregister.length > 0) {
                        yield Promise.all(regsToUnregister.map(r => r.unregister()));
                        Logger_1.default.log(new TelemetryEvents_1.ServiceWorkerUnregistrationEvent({
                            unregisteredCount: regsToUnregister.length,
                        }));
                    }
                }
                catch (err) {
                    const error = err;
                    const firstReg = regsToUnregister[0];
                    const message = formatSwError({
                        operation: "unregister",
                        scope: firstReg === null || firstReg === void 0 ? void 0 : firstReg.scope,
                        state: (_a = firstReg === null || firstReg === void 0 ? void 0 : firstReg.active) === null || _a === void 0 ? void 0 : _a.state,
                        error: (error === null || error === void 0 ? void 0 : error.message) || "",
                    });
                    Logger_1.default.log(new TelemetryEvents_1.ServiceWorkerErrorEvent({
                        message,
                        error: "sw_unregister_failed",
                        swOperation: "unregister",
                        swScope: firstReg === null || firstReg === void 0 ? void 0 : firstReg.scope,
                        swState: (_b = firstReg === null || firstReg === void 0 ? void 0 : firstReg.active) === null || _b === void 0 ? void 0 : _b.state,
                        swErrorType: error === null || error === void 0 ? void 0 : error.name,
                        swErrorCategory: getSwErrorCategory(error),
                        isSecureContext: window.isSecureContext,
                    }));
                }
            }
        });
    }
    setupMessageListener() {
        if ("serviceWorker" in navigator) {
            navigator.serviceWorker.addEventListener("message", event => {
                this.handleServiceWorkerMessage(event);
            });
        }
    }
    handleServiceWorkerMessage(event) {
        var _a;
        // Extract message type once for validation and error reporting
        const messageType = typeof (event === null || event === void 0 ? void 0 : event.data) === "object" && typeof ((_a = event.data) === null || _a === void 0 ? void 0 : _a.type) === "string"
            ? event.data.type
            : undefined;
        try {
            if (!messageType) {
                return;
            }
            if (messageType === "METRICS") {
                this.processMetrics(event.data.metrics);
            }
        }
        catch (err) {
            // Silently fail - don't break on telemetry errors
            const error = err;
            const message = formatSwError({
                operation: "message",
                error: (error === null || error === void 0 ? void 0 : error.message) || "",
            });
            Logger_1.default.log(new TelemetryEvents_1.ServiceWorkerErrorEvent({
                message,
                error: "sw_metrics_processing_failed",
                swOperation: "message",
                swMessageType: messageType,
                swErrorType: error === null || error === void 0 ? void 0 : error.name,
                swErrorCategory: getSwErrorCategory(error),
            }));
        }
    }
    processMetrics(metrics) {
        // Calculate aggregated statistics
        const aggregated = this.aggregateMetrics(metrics);
        // Log to telemetry
        Logger_1.default.log(new TelemetryEvents_1.ServiceWorkerCachePerformanceEvent(aggregated));
    }
    aggregateMetrics(metrics) {
        const calculateStats = (strategy) => {
            const totalOps = strategy.operationCount;
            const hitRatio = totalOps > 0 ? (strategy.cacheHits / totalOps) * 100 : 0;
            const avgLatency = totalOps > 0 ? strategy.totalLatencyMs / totalOps : 0;
            return {
                hits: strategy.cacheHits,
                misses: strategy.cacheMisses,
                hitRatio: Math.round(hitRatio * 100) / 100, // 2 decimal places
                avgLatencyMs: Math.round(avgLatency * 100) / 100,
                operations: totalOps,
                timeouts: strategy.networkTimeouts || 0,
            };
        };
        const collectionDurationMs = Date.now() - metrics.collectionStartTime;
        return {
            networkFirst: calculateStats(metrics.networkFirst),
            staleWhileRevalidate: calculateStats(metrics.staleWhileRevalidate),
            cacheFirst: calculateStats(metrics.cacheFirst),
            trimCount: metrics.cacheTrim.trimCount,
            entriesDeleted: metrics.cacheTrim.entriesDeleted,
            collectionDurationMs,
        };
    }
    createServiceWorkerURL(serviceWorkerPath) {
        const parsedURL = new URL(serviceWorkerPath, window.location.origin);
        if (parsedURL.origin === window.location.origin) {
            return parsedURL;
        }
        throw new Error(`invalid swURL: ${parsedURL.href}`);
    }
    /**
     * Determines the appropriate scope for the Service Worker.
     * Restricts the SW to only control the launcher directory to prevent overly broad control.
     *
     * For production URLs like /dl/launcher/launcher.html, scope is /dl/launcher/
     * For local dev URLs like /launcher.html, scope is /
     */
    getServiceWorkerScope() {
        const pathname = window.location.pathname;
        // Extract directory path from current location
        // e.g., "/dl/launcher/launcher.html" -> "/dl/launcher/"
        const lastSlash = pathname.lastIndexOf("/");
        if (lastSlash > 0) {
            return pathname.substring(0, lastSlash + 1);
        }
        // Fallback to root for local dev (e.g., "/launcher.html" -> "/")
        return "/";
    }
    register() {
        return __awaiter(this, void 0, void 0, function* () {
            const featureFlags = FeaturesService_1.default.getFeatureFlags();
            if (!("serviceWorker" in navigator)) {
                return;
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const tt = window.trustedTypes;
            const trustedScriptUrlPolicy = tt
                ? tt.createPolicy("@msteams/join-launcher#register-service-worker", {
                    createScriptURL: (url) => this.createServiceWorkerURL(url),
                })
                : undefined;
            let baseUrl = "service-worker.js";
            if (featureFlags.forwardContainerQSPToServiceWorker) {
                const currentUrl = new URL(window.location.href);
                const containerQSP = currentUrl.searchParams.get("container");
                const versionQSP = currentUrl.searchParams.get("version");
                if (containerQSP) {
                    const sep = baseUrl.includes("?") ? "&" : "?";
                    baseUrl += `${sep}container=${encodeURIComponent(containerQSP)}`;
                }
                if (versionQSP) {
                    const sep = baseUrl.includes("?") ? "&" : "?";
                    baseUrl += `${sep}version=${encodeURIComponent(versionQSP)}`;
                }
            }
            const serviceWorkerPath = new URL(baseUrl, window.location.href);
            // Determine scope from current location to restrict SW to launcher directory only
            const scope = this.getServiceWorkerScope();
            try {
                const registration = yield navigator.serviceWorker.register(trustedScriptUrlPolicy
                    ? trustedScriptUrlPolicy.createScriptURL(serviceWorkerPath.toString())
                    : serviceWorkerPath, { scope });
                if (registration) {
                    Logger_1.default.log(new TelemetryEvents_1.ServiceWorkerRegistrationEvent({
                        url: serviceWorkerPath.toString(),
                    }));
                    // Send config overrides first (Tier 3), then precache list
                    yield this.postConfigMessage(registration);
                    yield this.postPrecacheList(registration);
                }
            }
            catch (err) {
                const error = err;
                const message = formatSwError({
                    operation: "register",
                    url: serviceWorkerPath.pathname,
                    scope,
                    error: (error === null || error === void 0 ? void 0 : error.message) || "",
                });
                Logger_1.default.log(new TelemetryEvents_1.ServiceWorkerErrorEvent({
                    message,
                    error: "sw_register_failed",
                    swOperation: "register",
                    swUrl: serviceWorkerPath.pathname,
                    swScope: scope,
                    swErrorType: error === null || error === void 0 ? void 0 : error.name,
                    swErrorCategory: getSwErrorCategory(error),
                    isSecureContext: window.isSecureContext,
                }));
            }
        });
    }
    /**
     * Source of precache assets. Split out for testability; webpack replaces the constant.
     */
    resolvePrecacheAssets() {
        return PRECACHE_ASSETS;
    }
    /**
     * Source of statics path. Split out for testability; PDS replaces the constant in html file.
     */
    getStaticsPath() {
        return STATICS_PATH;
    }
    /**
     * Get the list of static assets to precache
     */
    getPrecacheStaticAssets() {
        const assets = [];
        for (const asset of this.resolvePrecacheAssets()) {
            if (asset.startsWith("https://")) {
                assets.push(asset);
            }
            else {
                assets.push(`${this.getStaticsPath()}${asset}`);
            }
        }
        return assets;
    }
    /**
     * Send CONFIG message with runtime overrides (Tier 3)
     */
    postConfigMessage(reg) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            const featureFlags = FeaturesService_1.default.getFeatureFlags();
            // Build runtime config from feature flags (QSP overrides, ECS, etc.)
            const runtimeConfig = {};
            if (typeof featureFlags.serviceWorkerCacheMaxAge === "number") {
                runtimeConfig.serviceWorkerCacheMaxAge = featureFlags.serviceWorkerCacheMaxAge;
            }
            if (typeof featureFlags.serviceWorkerCacheMaxEntries === "number") {
                runtimeConfig.serviceWorkerCacheMaxEntries = featureFlags.serviceWorkerCacheMaxEntries;
            }
            if (typeof featureFlags.serviceWorkerNetworkTimeout === "number") {
                runtimeConfig.serviceWorkerNetworkTimeout = featureFlags.serviceWorkerNetworkTimeout;
            }
            if (typeof featureFlags.serviceWorkerCacheName === "string") {
                runtimeConfig.serviceWorkerCacheName = featureFlags.serviceWorkerCacheName;
            }
            if (typeof featureFlags.serviceWorkerEnableCachePerformanceTelemetry === "boolean") {
                runtimeConfig.serviceWorkerEnableCachePerformanceTelemetry =
                    featureFlags.serviceWorkerEnableCachePerformanceTelemetry;
            }
            if (typeof featureFlags.serviceWorkerMetricsReportInterval === "number") {
                runtimeConfig.serviceWorkerMetricsReportInterval =
                    featureFlags.serviceWorkerMetricsReportInterval;
            }
            if (typeof featureFlags.serviceWorkerMetricsReportOperationThreshold === "number") {
                runtimeConfig.serviceWorkerMetricsReportOperationThreshold =
                    featureFlags.serviceWorkerMetricsReportOperationThreshold;
            }
            if (typeof featureFlags.serviceWorkerMetricsDebounceDelay === "number") {
                runtimeConfig.serviceWorkerMetricsDebounceDelay =
                    featureFlags.serviceWorkerMetricsDebounceDelay;
            }
            // Only send if there are runtime overrides
            if (Object.keys(runtimeConfig).length === 0) {
                return;
            }
            try {
                const serviceWorkers = [reg.active, reg.waiting, reg.installing];
                for (const worker of serviceWorkers) {
                    if (!worker) {
                        continue;
                    }
                    worker.postMessage({ type: "CONFIG", config: runtimeConfig });
                }
            }
            catch (err) {
                const error = err;
                const message = formatSwError({
                    operation: "message",
                    scope: reg.scope,
                    state: (_a = reg.active) === null || _a === void 0 ? void 0 : _a.state,
                    error: (error === null || error === void 0 ? void 0 : error.message) || "",
                });
                Logger_1.default.log(new TelemetryEvents_1.ServiceWorkerErrorEvent({
                    message,
                    error: "sw_config_message_failed",
                    swOperation: "message",
                    swMessageType: "CONFIG",
                    swScope: reg.scope,
                    swState: (_b = reg.active) === null || _b === void 0 ? void 0 : _b.state,
                    swErrorType: error === null || error === void 0 ? void 0 : error.name,
                    swErrorCategory: getSwErrorCategory(error),
                }));
            }
        });
    }
    postPrecacheList(reg) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            const assets = this.getPrecacheStaticAssets();
            if (assets.length === 0) {
                return; // Nothing to precache
            }
            try {
                const serviceWorkers = [reg.active, reg.waiting, reg.installing];
                for (const worker of serviceWorkers) {
                    if (!worker) {
                        continue;
                    }
                    worker.postMessage({ type: "PRECACHE", urls: assets });
                }
            }
            catch (err) {
                const error = err;
                const message = formatSwError({
                    operation: "message",
                    scope: reg.scope,
                    state: (_a = reg.active) === null || _a === void 0 ? void 0 : _a.state,
                    error: (error === null || error === void 0 ? void 0 : error.message) || "",
                });
                Logger_1.default.log(new TelemetryEvents_1.ServiceWorkerErrorEvent({
                    message,
                    error: "sw_precache_message_failed",
                    swOperation: "message",
                    swMessageType: "PRECACHE",
                    swScope: reg.scope,
                    swState: (_b = reg.active) === null || _b === void 0 ? void 0 : _b.state,
                    swErrorType: error === null || error === void 0 ? void 0 : error.name,
                    swErrorCategory: getSwErrorCategory(error),
                }));
            }
        });
    }
}
exports.ServiceWorkerController = ServiceWorkerController;
ServiceWorkerController._instance = null;
const serviceWorkerController = ServiceWorkerController.instance;
exports.default = serviceWorkerController;
